import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Checkbox,
  Stack,
  MenuItem,
  TextField,
  Box,
  FormControlLabel,
  FormControl,
  Typography,
  Grid,
  Link,
} from '@mui/material';

// CONFIG
import Config from '../../../../Config';

// NETWORK
import useDeviceRequest from '../../../../requests/DeviceRequest';

// MESSAGES
import DeviceFormattedMessages from '../../../../messages/admin/DeviceFormattedMessages';
import BackButton from '../../../common/BackButton';
import Loader from '../../../common/Loader';
import Paginator from '../../../common/Paginator';
import AdvancedFiltersContainer from '../../../common/AdvancedFiltersContainer';

export default function DeviceAllList({
  errorHandler,
  addNewDevice,
  showSingleDevice,
  backButtonClick,
}) {
  // Replace state with useState
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(Config.defaultPL);
  const [searchParams, setSearchParams] = useState({
    isCurrentlyDedicated: false,
    isCurrentlyPooled: false,
    deviceIdFilter: '',
    deviceNameFilter: '',
    typeFilter: '',
    statusFilter: '',
    replyTnFilter: '',
  });
  const [open, setOpen] = useState(false);
  const [filteredText, setFilteredText] = useState([]);
  const [reset, setReset] = useState(false);
  // Add debounce timer ref
  const debounceTimer = useRef(null);

  const { getAllDevices } = useDeviceRequest();

  const fetchDevices = useCallback(async () => {
    setLoading(true);
    const devices = await getAllDevices(searchParams, errorHandler);
    console.log('Got ALL Devices:', devices);
    setDevices(devices);
    setLoading(false);
  }, [reset, pageNumber]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Handle checkbox
  const handleCheckbox = (event) => {
    const { checked, name } = event.target;

    setSearchParams((prev) => ({
      ...prev,
      [name]: checked,
    }));

    setPageNumber(1);
    setReset(!reset);
  };

  // Change page length
  const changePageLength = (event) => {
    const value = event.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  // Handle page change
  const changeEvent = (value) => {
    setPageNumber(value);
  };

  // Handle input change
  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setSearchParams((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear any existing timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set new timer to trigger search after 500ms
    debounceTimer.current = setTimeout(() => {
      setPageNumber(1);
      setReset(!reset);
    }, 500);
  };

  // Clear filters
  const clearFilters = () => {
    setSearchParams({
      ...searchParams,
      deviceIdFilter: '',
      deviceNameFilter: '',
      typeFilter: '',
      statusFilter: '',
      replyTnFilter: '',
    });

    const filterForm = document.getElementById('filter-form');
    if (filterForm) {
      filterForm.reset();
    }
    setPageNumber(1);
    setReset(!reset);
  };

  /*
    Generates the Search Tools for querying the data
  */
  const generateSearchText = useCallback(() => {
    const filterText = [];
    const filterValues = [
      'Device Id:',
      'Device Name:',
      'Type:',
      'Status:',
      'Reply Tn:',
    ];

    [
      searchParams.deviceIdFilter,
      searchParams.deviceNameFilter,
      searchParams.typeFilter,
      searchParams.statusFilter,
      searchParams.replyTnFilter,
    ].forEach((filter, i) => {
      if (filter && filter !== '')
        filterText.push(
          <span key={i}>
            {filterText && filterText.length === 0 ? '' : ', '}
            <strong>{filterValues[i]}</strong> {filter}
          </span>
        );
    });

    setFilteredText(filterText);
  }, [searchParams]);

  useEffect(() => {
    generateSearchText();
  }, [generateSearchText]);

  // Generate checkbox
  const generateCheckbox = () => {
    return (
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={searchParams.isCurrentlyDedicated}
              onChange={handleCheckbox}
              name="isCurrentlyDedicated"
            />
          }
          label="Currently Dedicated"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={searchParams.isCurrentlyPooled}
              onChange={handleCheckbox}
              name="isCurrentlyPooled"
            />
          }
          label="Currently Pooled"
        />
      </Box>
    );
  };

  // Generate lander
  const generateLander = () => {
    return (
      <Stack gap={2}>
        <Box>
          <BackButton onClick={() => backButtonClick()} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h4">All Devices</Typography>
          <Button onClick={addNewDevice} variant="contained" color="secondary">
            {DeviceFormattedMessages.addNewText}
          </Button>
        </Box>
      </Stack>
    );
  };

  // Generate table
  const generateTable = () => {
    if (loading) return <Loader />;

    if (devices.length === 0)
      return <Typography>No Devices Retrieved.</Typography>;

    return (
      <>
        <Stack>
          {filteredText && filteredText.length > 0 && (
            <Typography fontSize={12} marginBottom={1}>
              {filteredText}
            </Typography>
          )}
        </Stack>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{DeviceFormattedMessages.deviceId}</TableCell>
                <TableCell>{DeviceFormattedMessages.deviceName}</TableCell>
                <TableCell>{DeviceFormattedMessages.priority}</TableCell>
                <TableCell>{DeviceFormattedMessages.type}</TableCell>
                <TableCell>{DeviceFormattedMessages.status}</TableCell>
                <TableCell>{DeviceFormattedMessages.replyTn}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices
                .slice(
                  (pageNumber - 1) * pageLength,
                  (pageNumber - 1) * pageLength + pageLength
                )
                .map((device, index) => {
                  const {
                    device_id,
                    device_name,
                    priority,
                    device_type,
                    device_status,
                    reply_tn,
                    device_status_meaning,
                    device_type_meaning,
                  } = device;

                  const deviceId = device_id || 'missing';
                  return (
                    <TableRow key={device_id} id={device_id}>
                      <TableCell>{deviceId}</TableCell>
                      <TableCell>
                        <Link
                          tabIndex={0}
                          onClick={() => showSingleDevice(device, true)}
                        >
                          {device_name}
                        </Link>
                      </TableCell>
                      <TableCell>{priority}</TableCell>
                      <TableCell>
                        {device_type_meaning || device_type}
                      </TableCell>
                      <TableCell>
                        {device_status_meaning || device_status}
                      </TableCell>
                      <TableCell>{reply_tn || 'N/A'}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <Paginator
          itemCount={devices.length}
          pageNumber={pageNumber}
          changeEvent={changeEvent}
          pageLength={pageLength}
          editFunction={changePageLength}
        />
      </>
    );
  };

  // Type dropdown
  const typeDropdown = () => {
    const deviceType = [
      'Inbound',
      'Outbound',
      'Both',
      'Unavailable',
      'Virtual',
      'Dedicated',
      'Customer Pool',
    ];

    return (
      <TextField
        select
        id="typeFilter"
        value={searchParams.typeFilter || ''}
        onChange={handleInputChange}
        name="typeFilter"
        label="Type"
        fullWidth
        size="small"
      >
        {!searchParams.typeFilter && <MenuItem value="">Select Type</MenuItem>}
        {deviceType.map((type) => (
          <MenuItem key={type} value={type}>
            {type}
          </MenuItem>
        ))}
      </TextField>
    );
  };

  // Status dropdown
  const statusDropdown = () => {
    const deviceStatus = ['Active', 'Unavailable', 'Expired'];

    return (
      <FormControl fullWidth variant="standard">
        <TextField
          select
          id="statusFilter"
          value={searchParams.statusFilter || ''}
          onChange={handleInputChange}
          name="statusFilter"
          label="Status"
          fullWidth
          size="small"
        >
          {!searchParams.statusFilter && (
            <MenuItem value="">Select Status</MenuItem>
          )}
          {deviceStatus.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </FormControl>
    );
  };

  // Generate search tools
  const generateSearchTools = () => {
    return (
      <>
        <AdvancedFiltersContainer setOpen={setOpen} open={open}>
          <Box
            sx={{
              maxWidth: { lg: 674 },
            }}
          >
            <form id="filter-form">
              <Stack
                sx={{
                  flexDirection: { lg: 'row' },
                  gap: 2,
                  marginY: 2,
                }}
              >
                <Stack>
                  <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                    By Field:
                  </Typography>
                </Stack>

                <Stack sx={{ gap: 1, flex: 1 }}>
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      label="Device Id"
                      name="deviceIdFilter"
                      value={searchParams.deviceIdFilter || ''}
                      onChange={handleInputChange}
                      placeholder="Search Id"
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Device Name"
                      name="deviceNameFilter"
                      value={searchParams.deviceNameFilter || ''}
                      onChange={handleInputChange}
                      placeholder="Search Name"
                      fullWidth
                      size="small"
                    />
                    {typeDropdown()}{' '}
                  </Stack>
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    {statusDropdown()}

                    <TextField
                      label="Reply TN"
                      name="replyTnFilter"
                      value={searchParams.replyTnFilter || ''}
                      onChange={handleInputChange}
                      placeholder="Search Number"
                      fullWidth
                      size="small"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </form>
          </Box>
        </AdvancedFiltersContainer>

        <Stack
          sx={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 2,
            maxWidth: { lg: 674 },
          }}
        >
          {filteredText && filteredText.length > 0 && (
            <Button
              color="secondary"
              onClick={clearFilters}
              sx={{
                textTransform: 'none',
                textDecoration: 'underline',
                fontWeight: 400,
              }}
            >
              {DeviceFormattedMessages.clear}
            </Button>
          )}
        </Stack>
      </>
    );
  };

  // Render
  return (
    <Box>
      {generateLander()}
      {generateCheckbox()}
      <Grid container sx={{ justifyContent: 'space-between', rowGap: 2 }}>
        <Grid size={12}>{generateSearchTools()}</Grid>
        <Grid size={12}>{generateTable()}</Grid>
      </Grid>
    </Box>
  );
}
